import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { suppliersAPI } from '@/lib/api-client'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Trash2, Plus } from 'lucide-react'

interface InstallmentRow {
  installmentNumber: number
  amount: string
  dueDate: string
  notes: string
}

interface Props {
  open: boolean
  onClose: () => void
  orderId: string
  totalAmount: number
  existingInstallments?: any[]
}

export function InstallmentsDialog({ open, onClose, orderId, totalAmount, existingInstallments = [] }: Props) {
  const queryClient = useQueryClient()

  const buildInitialRows = (): InstallmentRow[] => {
    if (existingInstallments.length > 0) {
      return existingInstallments.map((inst: any) => ({
        installmentNumber: inst.installmentNumber,
        amount: parseFloat(inst.amount).toFixed(2),
        dueDate: inst.dueDate ? inst.dueDate.slice(0, 10) : '',
        notes: inst.notes ?? '',
      }))
    }
    return [{ installmentNumber: 1, amount: totalAmount.toFixed(2), dueDate: '', notes: '' }]
  }

  const [rows, setRows] = useState<InstallmentRow[]>(buildInitialRows)
  const [count, setCount] = useState('')
  const [startDate, setStartDate] = useState('')

  const saveMutation = useMutation({
    mutationFn: () =>
      suppliersAPI.setInstallments(orderId, {
        installments: rows.map((r) => ({
          installmentNumber: r.installmentNumber,
          amount: parseFloat(r.amount),
          dueDate: r.dueDate,
          notes: r.notes || undefined,
        })),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-order', orderId] })
      toast.success('Parcelas salvas com sucesso')
      onClose()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  function generateEqual() {
    const n = parseInt(count)
    if (!n || n < 1 || !startDate) {
      toast.error('Informe a quantidade de parcelas e a data inicial')
      return
    }
    const perInstallment = totalAmount / n
    const newRows: InstallmentRow[] = []
    for (let i = 0; i < n; i++) {
      const d = new Date(startDate + 'T00:00:00')
      d.setMonth(d.getMonth() + i)
      newRows.push({
        installmentNumber: i + 1,
        amount: perInstallment.toFixed(2),
        dueDate: d.toISOString().slice(0, 10),
        notes: '',
      })
    }
    setRows(newRows)
  }

  function addRow() {
    setRows((prev) => [
      ...prev,
      { installmentNumber: prev.length + 1, amount: '', dueDate: '', notes: '' },
    ])
  }

  function removeRow(idx: number) {
    setRows((prev) =>
      prev
        .filter((_, i) => i !== idx)
        .map((r, i) => ({ ...r, installmentNumber: i + 1 })),
    )
  }

  function updateRow(idx: number, field: keyof InstallmentRow, value: string) {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)))
  }

  const rowsTotal = rows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0)
  const diff = Math.abs(rowsTotal - totalAmount)
  const hasDiff = diff > 0.01

  function handleSave() {
    if (rows.some((r) => !r.amount || !r.dueDate)) {
      toast.error('Preencha valor e vencimento de todas as parcelas')
      return
    }
    saveMutation.mutate()
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Gerenciar Parcelas</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Quick generate */}
          <div className="rounded-lg border bg-neutral-50 p-3 space-y-2">
            <p className="text-sm font-medium text-neutral-700">Gerar parcelas iguais</p>
            <div className="flex items-end gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Quantidade</Label>
                <Input
                  type="number"
                  min={1}
                  value={count}
                  onChange={(e) => setCount(e.target.value)}
                  placeholder="Ex: 3"
                  className="h-8 w-24 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Data da 1ª parcela</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <Button size="sm" variant="outline" onClick={generateEqual} className="h-8">
                Gerar
              </Button>
            </div>
          </div>

          {/* Table */}
          <div className="rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 w-10">Nº</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500">Vencimento</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500">Valor (R$)</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500">Obs.</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((row, idx) => (
                  <tr key={idx}>
                    <td className="px-3 py-1.5 text-neutral-500">{row.installmentNumber}</td>
                    <td className="px-3 py-1.5">
                      <Input
                        type="date"
                        value={row.dueDate}
                        onChange={(e) => updateRow(idx, 'dueDate', e.target.value)}
                        className="h-7 text-xs"
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <Input
                        type="number"
                        min={0}
                        step={0.01}
                        value={row.amount}
                        onChange={(e) => updateRow(idx, 'amount', e.target.value)}
                        className="h-7 text-xs w-28"
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <Input
                        value={row.notes}
                        onChange={(e) => updateRow(idx, 'notes', e.target.value)}
                        placeholder="Opcional"
                        className="h-7 text-xs"
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6 text-neutral-400 hover:text-red-500"
                        onClick={() => removeRow(idx)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <Button size="sm" variant="outline" onClick={addRow} className="gap-1 h-8">
              <Plus className="h-3.5 w-3.5" /> Adicionar parcela
            </Button>
            <div className="text-sm">
              <span className="text-neutral-500">Total parcelas: </span>
              <span className={hasDiff ? 'text-red-600 font-medium' : 'text-neutral-700 font-medium'}>
                R$ {rowsTotal.toFixed(2)}
              </span>
              {hasDiff && (
                <span className="text-red-500 text-xs ml-2">
                  (diferença de R$ {diff.toFixed(2)} do pedido R$ {totalAmount.toFixed(2)})
                </span>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saveMutation.isPending || rows.length === 0}>
            {saveMutation.isPending ? 'Salvando...' : 'Salvar Parcelas'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
